import { createSelector } from 'reselect';

import { PersonFields } from '../models/person';
import { AddressFields } from '../../types/address';
import { LoadingState } from '../declarations/widget-ums';
import { getTextValue, getValue } from '../constants/getFieldValues';
import { OMKError } from '../errors/OMK';
import {
  FIAS_LEVEL_HOUSE,
  isNewMoscowArea,
  isSubsequentRequestError,
  ZEL_AO,
} from '../constants/address';
import { OMK_SPECIAL_CASE } from '../constants/omkSpecialCases';
import { checkAddress } from '../helpers/address';
import { notEmptyField } from '../validators/common';

// `person` - Специальный кейс для валидаций
export type PersonModel = 'applicant' | 'spouse' | 'child' | 'person';

export interface PersonWithAddress {
  [key: string]: Pick<PersonFields, 'address'>;
}

export interface Props {
  model: PersonModel;
}

type PropsWithForManualInput = Props & { forManualInput?: boolean };

const isComponentForManualInput: (
  _,
  { forManualInput }: { forManualInput?: boolean }
) => boolean = (_, { forManualInput }) => Boolean(forManualInput);

const isExpandedView: (
  _,
  { isExpandedView }: { isExpandedView?: boolean }
) => boolean = (_, { isExpandedView }) => Boolean(isExpandedView);

const getModel: (_, { model }: Props) => PersonModel = (_, { model }) => model;

export function getAddress(
  state: PersonWithAddress,
  { model }: Props
): AddressFields {
  return state[model].address;
}

export const getArea: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['area'] = createSelector(getAddress, ({ area }) => area);

export const getDistrict: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['district'] = createSelector(
  getAddress,
  ({ district, settlement }) =>
    getTextValue(settlement) ? settlement : district
);
export const getStreet: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['street'] = createSelector(
  getAddress,
  ({ street }) => street
);

export const getAddressUnmodeledStreet: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['street']['value'] = createSelector(getStreet, (street) =>
  getValue(street)
);

export const getBuilding: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['building'] = createSelector(
  getAddress,
  ({ building }) => building
);
export const getHouse: (
  state: PersonWithAddress,
  props: Props
) =>
  | AddressFields['house']
  | AddressFields['ownership'] = createSelector(
  getAddress,
  ({ house, ownership }) => (getValue(house) || house.error ? house : ownership)
);
export const getStructure: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['structure'] = createSelector(
  getAddress,
  ({ structure }) => structure
);

export const isNewMoscow = createSelector(getArea, (area) => {
  return isNewMoscowArea(getTextValue(area));
});

export const isZelArea = createSelector(getArea, (area) => {
  return getTextValue(area) === ZEL_AO;
});

export function getLoadingState({
  loading,
}: {
  loading: LoadingState;
}): LoadingState {
  return loading;
}

export const getAddressSearch: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['address'] = createSelector(
  getAddress,
  ({ address }) => address
);

export const getAddressSearchValue: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['address']['value'] = createSelector(
  getAddressSearch,
  getValue
);

export const getAddressSearchError: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['address']['error'] = createSelector(
  getAddressSearch,
  ({ error }) => error || false
);

export const getAddressSearchSuggestions: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['suggestions'] = createSelector(
  getAddress,
  ({ suggestions }) => suggestions
);

export const isAddressInManualMode: (
  state: PersonWithAddress,
  props: Props
) => boolean = createSelector(getAddress, ({ isManualInput }) => isManualInput);

export const isSubsequentRequestRunning: (
  state: {
    loading: LoadingState;
  },
  { model }: Props
) => boolean = createSelector(
  getLoadingState,
  getModel,
  ({ effects = {} }, model) =>
    [
      effects[`${model}/address/eirc/check`],
      effects[`${model}/address/omk/check`],
      effects[`${model}/address/set/street`],
      effects[`${model}/address/omk/check/street`],
    ].some(Boolean)
);

export const getSubsequentRequestError: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['subsequentRequestError'] = createSelector(
  getAddress,
  ({ subsequentRequestError }) => subsequentRequestError
);

export const isAddressCompleted = createSelector(
  getAddress,
  ({ isAddressCompleted }) => isAddressCompleted
);

export const isAddressSearchDisabled: (
  state: PersonWithAddress & { loading: LoadingState },
  props: Props
) => boolean = createSelector(
  isAddressInManualMode,
  isSubsequentRequestRunning,
  (isManualInput, isRequestInRunning) => isManualInput || isRequestInRunning
);

export const isAddressSpinnerVisible: (
  state: PersonWithAddress & { loading: LoadingState },
  props: PropsWithForManualInput
) => boolean = createSelector(
  isAddressInManualMode,
  isSubsequentRequestRunning,
  isComponentForManualInput,
  (isManualInput, isRequestRunning, forManualInput) =>
    isRequestRunning && isManualInput === forManualInput
);

export const isAddressSubsequentErrorVisible: (
  state: PersonWithAddress,
  props: Props & { forManualInput?: boolean }
) => boolean = createSelector(
  getSubsequentRequestError,
  isAddressInManualMode,
  isComponentForManualInput,
  (error, isManualInput, forManualInput) =>
    isManualInput === forManualInput && isSubsequentRequestError(error)
);

export const isOMKErrorVisible: (
  state: PersonWithAddress,
  props: Props
) => boolean = createSelector(getSubsequentRequestError, (error) => {
  return error === OMKError.errorName;
});

export const isManualInputSwitchVisible: (
  state: PersonWithAddress,
  props: Props
) => boolean = createSelector(
  getAddress,
  isSubsequentRequestRunning,
  getSubsequentRequestError,
  isOMKErrorVisible,
  (address, isRequestRunning, subsequentRequestError, isOMKErrorVisible) => {
    if (isRequestRunning) return false;
    return [
      address.isManualInput,
      isSubsequentRequestError(subsequentRequestError),
      !isOMKErrorVisible && !checkAddress(address),
    ].some(Boolean);
  }
);

export const getFlat: (
  state: PersonWithAddress,
  props: Props
) => AddressFields['flat'] = createSelector(
  getAddress,
  getSubsequentRequestError,
  isSubsequentRequestRunning,
  ({ flat, isManualInput }, error, isSubsequentRequestRunning) => {
    return {
      ...flat,
      disabled:
        isSubsequentRequestRunning || (!isManualInput && Boolean(error)),
    };
  }
);

export const getFiasLevel: (
  state: PersonWithAddress,
  props: Props
) => number = createSelector(getAddress, ({ fiasLevel }) => fiasLevel);

export const isFullViewVisible: (
  state: PersonWithAddress,
  props: Props & { isExpanedView?: boolean }
) => boolean = createSelector(
  isAddressInManualMode,
  isExpandedView,
  (isManualInput, isExpandedView) => isManualInput || isExpandedView
);

export const flatIsVisible: (
  state: PersonWithAddress & { loading: LoadingState },
  props: Props & { forManualInput?: boolean; isExpandedView?: boolean }
) => boolean = createSelector(
  isSubsequentRequestRunning,
  isComponentForManualInput,
  isFullViewVisible,
  getFiasLevel,
  getSubsequentRequestError,
  (requestIsRunning, forManualInput, inFullView, fiasLevel, error) => {
    if (forManualInput) return true;
    if (requestIsRunning || (error && error !== OMKError.errorName))
      return false;
    if (fiasLevel !== FIAS_LEVEL_HOUSE) return false;
    return !inFullView;
  }
);

export const isAddressFullViewSwitchVisible: (
  state: PersonWithAddress,
  props: Props
) => boolean = createSelector(
  isSubsequentRequestRunning,
  isAddressInManualMode,
  getFiasLevel,
  getSubsequentRequestError,
  (requestIsRunning, isManual, fiasLevel, error) => {
    if (requestIsRunning || (error && error !== OMKError.errorName))
      return false;
    return !isManual && fiasLevel === FIAS_LEVEL_HOUSE;
  }
);

export const isAddressSubregionFieldsVisible: (
  state: PersonWithAddress & { loading: LoadingState },
  props: Props
) => boolean = createSelector(
  getDistrict,
  (district) => !!getTextValue(district)
);

export const isAddressNewMoscowWarningVisible: (
  state: PersonWithAddress,
  props: Props
) => boolean = createSelector(
  getArea,
  getDistrict,
  getStreet,
  getHouse,
  getBuilding,
  getFlat,
  (area, district, ...fields) =>
    Boolean(
      getTextValue(area) &&
        getTextValue(district) &&
        [area, district, ...fields].filter(({ error }) => Boolean(error)).length
    )
);

export const isManualInputDisabled = createSelector(
  isExpandedView,
  isSubsequentRequestRunning,
  (isExpanded, isRequestRunning) => isExpanded || isRequestRunning
);

export const getSpecialCaseForParseOmkCode = createSelector(
  isNewMoscow,
  isZelArea,
  isAddressInManualMode,
  (isNewMoscow, isZelArea, isAddressInManualMode) => {
    if (isNewMoscow) return OMK_SPECIAL_CASE.newMoscow;
    if (isZelArea) return OMK_SPECIAL_CASE.zelao;
    if (isAddressInManualMode) return OMK_SPECIAL_CASE.manualInput;
    return '';
  }
);

export const getDisableContinueButton = createSelector(
  isOMKErrorVisible,
  isSubsequentRequestRunning,
  getSubsequentRequestError,
  (...values) => values.some(Boolean)
);

export const missingAddressInfo: (
  state: PersonWithAddress,
  { model }: Props
) => boolean = createSelector(
  getAddress,
  getArea,
  getDistrict,
  isAddressInManualMode,
  (address, area, district, isManualInput) => {
    const addressCompleted = checkAddress(address);
    const areaValue = notEmptyField(getTextValue(area));
    const districtValue = notEmptyField(getTextValue(district));
    return addressCompleted && (areaValue || districtValue) && !isManualInput;
  }
);
